import React, { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ModalRoute } from "../routing/ModalRoute";
import Card from "../ui/Card";
import Button from "../ui/Button";
import { useMailboxScope } from "../../contexts/MailboxScopeContext";
import { leadsService } from "../../services/leads";
import { mailboxService, mailboxesQueryKey } from "../../services/mailbox";
import { resolveScopedMailbox } from "../../utils/mailboxScope";
import { InboxAuditPipeline } from "./InboxAuditPipeline.jsx";

const RESULTS_BUTTON_STATUSES = new Set(["partially_completed", "completed"]);

/** Poll /progress until the task settles (completed, failed, or timed out). */
const PROGRESS_POLL_STATUSES = new Set(["pending", "running", "partially_completed"]);

/** Fetch /insights while partial results are available, and once when fully complete. */
const INSIGHTS_POLL_STATUSES = new Set(["partially_completed"]);
const INSIGHTS_ENABLED_STATUSES = new Set(["partially_completed", "completed"]);

function useBulkTaskProgress(taskHash) {
  return useQuery({
    queryKey: ["bulk-task-progress", taskHash],
    queryFn: () => leadsService.getBulkTaskProgress(taskHash),
    enabled: Boolean(taskHash),
    refetchInterval: (query) => {
      if (query.state.fetchStatus === "fetching") return false;
      if (query.state.status === "error") return false;
      const st = query.state.data?.status;
      if (st == null) return 5000;
      return PROGRESS_POLL_STATUSES.has(st) ? 5000 : false;
    },
  });
}

const InboxAuditContent = ({ taskHash, progressQuery }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const mailboxScope = useMailboxScope();

  const mailboxesQuery = useQuery({
    queryKey: mailboxesQueryKey,
    queryFn: mailboxService.list,
  });

  const progressStatus = progressQuery.data?.status;

  useQuery({
    queryKey: ["bulk-task-insights", taskHash],
    queryFn: () => leadsService.getBulkTaskInsights(taskHash),
    enabled: Boolean(taskHash) && INSIGHTS_ENABLED_STATUSES.has(progressStatus),
    refetchInterval: (query) => {
      if (query.state.fetchStatus === "fetching") return false;
      if (query.state.status === "error") return false;
      if (!INSIGHTS_POLL_STATUSES.has(progressStatus)) return false;
      return 4000;
    },
  });

  useEffect(() => {
    if (progressStatus === "completed" && taskHash) {
      queryClient.invalidateQueries({ queryKey: ["bulk-task-insights", taskHash] });
    }
  }, [progressStatus, taskHash, queryClient]);

  useEffect(() => {
    const n = progressQuery.data?.lead_found;
    if (n >= 1 && taskHash) {
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["mailbox-audit-results"] });
    }
  }, [progressQuery.data?.lead_found, taskHash, queryClient]);

  const mailboxes = mailboxesQuery.data?.results || [];
  const activeMailbox = resolveScopedMailbox(mailboxes, mailboxScope?.mailboxHash);
  const connected = mailboxes.filter((m) => m.connected);

  const refreshAuditQueries = () => {
    queryClient.invalidateQueries({ queryKey: ["leads"] });
    queryClient.invalidateQueries({ queryKey: ["bulk-email-tasks"] });
    queryClient.invalidateQueries({ queryKey: ["mailbox-audit-results"] });
  };

  const goResults = () => {
    refreshAuditQueries();
    navigate("/audit/");
  };

  const goLeads = () => {
    refreshAuditQueries();
    navigate("/leads/");
  };

  const startMutation = useMutation({
    mutationFn: () => leadsService.processRecentMailbox(activeMailbox?.hash),
    onSuccess: (data) => {
      const h = data?.bulk_task_hash;
      if (h) {
        navigate(`/audit/new/${h}/`);
      }
      queryClient.invalidateQueries({ queryKey: ["bulk-email-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["mailbox-audit-results"] });
    },
  });

  const status = progressQuery.data?.status;
  const canViewResults = RESULTS_BUTTON_STATUSES.has(status);
  const leadCount = progressQuery.data?.lead_found || 0;

  return (
    <Card className="max-w-lg w-full p-8 relative">
      <Link
        to="/audit/"
        className="absolute top-4 right-4 text-theme-dim hover:text-theme-text font-mono text-xs"
        onClick={refreshAuditQueries}
      >
        Close
      </Link>
      <h3 className="font-display font-bold italic text-xl text-theme-on-background mb-3">New inbox audit</h3>
      <p className="text-theme-muted text-sm mb-4">
        We will get your recent incoming emails from{" "}
        <span className="text-theme-text">{activeMailbox?.email || "your mailbox"}</span>, identify cold outreach,
        enrich company pages, and score matches against your brand ICP. This can take a few minutes.
      </p>
      {!activeMailbox?.connected ? (
        <p className="text-theme-muted text-sm mb-4">Connect a mailbox in Settings first, then try again.</p>
      ) : null}
      {taskHash ? (
        <InboxAuditPipeline data={progressQuery.data} />
      ) : null}
      <div className="flex flex-wrap gap-3">
        {activeMailbox?.connected && !taskHash ? (
          <Button type="button" loading={startMutation.isPending} onClick={() => startMutation.mutate()}>
            Run audit
          </Button>
        ) : null}
        {taskHash ? (
          <Button type="button" disabled={!canViewResults} onClick={goResults}>
            View Audit Results
          </Button>
        ) : null}
        {taskHash && leadCount >= 1 ? (
          <Button type="button" variant="secondary" disabled={!canViewResults} onClick={goLeads}>
            View {leadCount.toLocaleString()} {leadCount === 1 ? "lead" : "leads"}
          </Button>
        ) : null}
      </div>
      {connected.length === 0 ? (
        <p className="mt-4 text-theme-muted text-xs">No connected mailboxes.</p>
      ) : null}
    </Card>
  );
};

export default function InboxAuditModal() {
  const queryClient = useQueryClient();
  const { bulkTaskHash: taskHash = "" } = useParams();
  const progressQuery = useBulkTaskProgress(taskHash);

  return (
    <ModalRoute
      closeTo="/audit/"
      onClose={() => {
        queryClient.invalidateQueries({ queryKey: ["leads"] });
        queryClient.invalidateQueries({ queryKey: ["bulk-email-tasks"] });
        queryClient.invalidateQueries({ queryKey: ["bulk-task-insights"] });
        queryClient.invalidateQueries({ queryKey: ["mailbox-audit-results"] });
      }}
    >
      <InboxAuditContent taskHash={taskHash} progressQuery={progressQuery} />
    </ModalRoute>
  );
}
